import { useEffect, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { adminReports } from '@/api/admin';
import type { Money } from '@/api/types';
import type { PayoutStatus } from '@/api/extras';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from '@/components/ui/sonner';
import { ApiError } from '@/lib/http';
import { moneyToNumber } from '@/lib/format';

/**
 * 分润发放决定编辑器(状态 + 实发额 + 备注,一次提交)。
 *
 * 语义:**纯账务标记**,不打款、不改虚拟余额。考核未达标不影响金额计算 —— 是否发放由人判断
 * (系统停机 / App 更新等情形通常仍应照发),因此「不发放」强制填备注,结算争议时能回答为什么没发。
 */
export function PayoutEditor({
  userId,
  year,
  month,
  initial,
  defaultAmount,
}: {
  userId: string;
  year: number;
  month: number;
  initial: { status: PayoutStatus; paidAmount: Money | null; note: string | null };
  /** 系统计算的应分润(实发额默认值)。 */
  defaultAmount: Money;
}) {
  const qc = useQueryClient();
  const [status, setStatus] = useState<PayoutStatus>(initial.status);
  const [amount, setAmount] = useState<string>(
    initial.paidAmount ? moneyToNumber(initial.paidAmount).toFixed(2) : moneyToNumber(defaultAmount).toFixed(2),
  );
  const [note, setNote] = useState(initial.note ?? '');

  // 切换用户 / 刷新数据后同步表单初值。
  useEffect(() => {
    setStatus(initial.status);
    setAmount(
      initial.paidAmount ? moneyToNumber(initial.paidAmount).toFixed(2) : moneyToNumber(defaultAmount).toFixed(2),
    );
    setNote(initial.note ?? '');
  }, [userId, initial.status, initial.paidAmount, initial.note, defaultAmount]);

  const save = useMutation({
    mutationFn: () => {
      const value = Math.round(Number(amount) * 100);
      return adminReports.profitSharePayouts(year, month, [
        {
          userId,
          status,
          paidAmount: status === 'PAID' ? { value: Number.isFinite(value) ? value : 0, digits: 2 } : null,
          note: note.trim() || null,
        },
      ]);
    },
    onSuccess: () => {
      toast.success('已保存发放决定');
      qc.invalidateQueries({ queryKey: ['admin', 'profit-share', year, month] });
      qc.invalidateQueries({ queryKey: ['admin', 'profit-share-user', userId, year, month] });
    },
    onError: (e) => toast.error('保存失败', { description: e instanceof ApiError ? e.message : '' }),
  });

  const amountInvalid = status === 'PAID' && !(Number(amount) >= 0);
  const noteMissing = status === 'WITHHELD' && !note.trim();

  return (
    <div className="flex flex-wrap items-end gap-3 rounded-md border border-line bg-bg-2 p-3">
      <div className="space-y-1">
        <label className="text-xs text-ink-2">发放决定</label>
        <select
          value={status}
          onChange={(e) => setStatus(e.target.value as PayoutStatus)}
          className="block h-9 rounded-md border border-line bg-bg-3 px-2 text-sm text-ink-1"
        >
          <option value="PENDING">待定</option>
          <option value="PAID">已发放</option>
          <option value="WITHHELD">不发放</option>
        </select>
      </div>
      <div className="space-y-1">
        <label className="text-xs text-ink-2">实发额(美元)</label>
        <Input
          className="w-32 num"
          value={amount}
          disabled={status !== 'PAID'}
          onChange={(e) => setAmount(e.target.value.replace(/[^\d.-]/g, ''))}
        />
      </div>
      <div className="space-y-1 flex-1 min-w-[200px]">
        <label className="text-xs text-ink-2">备注{status === 'WITHHELD' && '(不发放必填)'}</label>
        <Input
          value={note}
          onChange={(e) => setNote(e.target.value)}
          placeholder="例如:当月系统停机 3 天,考核未达标照发"
        />
      </div>
      <Button disabled={save.isPending || amountInvalid || noteMissing} onClick={() => save.mutate()}>
        {save.isPending ? '保存中…' : '保存'}
      </Button>
    </div>
  );
}
